<template><markdown-wrapper><h1 id="timepicker">TimePicker <a class="header-anchor" href="#timepicker">&#128279;</a></h1>
<blockquote>
<p>A lightweight &amp; configurable time picker.</p>
</blockquote>
<p><ins class="adsbygoogle"
     style="display:block; text-align:center;"
     data-ad-layout="in-article"
     data-ad-format="fluid"
     data-ad-client="ca-pub-4714899946256166"
     data-ad-slot="4603582855"></ins></p>
<h2 id="example">Example <a class="header-anchor" href="#example">&#128279;</a></h2>
<p>To change the time input, you can:</p>
<ul>
<li>Click on the add / minus button.</li>
<li>Use up / down key of keyboard.</li>
<li>Use mouse wheel.</li>
<li>Input directly.</li>
</ul>
<p><strong>Note</strong>: Make sure to update the <code v-pre>v-model</code> reference when trying to change it from outside the component.</p>
<p>e.g. <code v-pre>time = new Date(time)</code></p>
<time-picker-example ref="time-picker-example"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">template</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">time-picker</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;time&quot;</span>/&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">script</span>&gt;</span><span class="language-javascript">
  <span class="hljs-keyword">export</span> <span class="hljs-keyword">default</span> {
    <span class="hljs-title function_">data</span> () {
      <span class="hljs-keyword">return</span> {
        <span class="hljs-attr">time</span>: <span class="hljs-keyword">new</span> <span class="hljs-title class_">Date</span>()
      }
    }
  }
</span><span class="hljs-tag">&lt;/<span class="hljs-name">script</span>&gt;</span>
<span class="hljs-comment">&lt;!-- time-picker-example.vue --&gt;</span>
</code></pre>
<h2 id="24-hour">24-hour <a class="header-anchor" href="#24-hour">&#128279;</a></h2>
<time-picker-24-example ref="time-picker-24-example"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">template</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">time-picker</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;time&quot;</span> <span class="hljs-attr">:show-meridian</span>=<span class="hljs-string">&quot;false&quot;</span>/&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">script</span>&gt;</span><span class="language-javascript">
  <span class="hljs-keyword">export</span> <span class="hljs-keyword">default</span> {
    <span class="hljs-title function_">data</span> () {
      <span class="hljs-keyword">return</span> {
        <span class="hljs-attr">time</span>: <span class="hljs-keyword">new</span> <span class="hljs-title class_">Date</span>()
      }
    }
  }
</span><span class="hljs-tag">&lt;/<span class="hljs-name">script</span>&gt;</span>
<span class="hljs-comment">&lt;!-- time-picker-24-example.vue --&gt;</span>
</code></pre>
<h2 id="range-limit">Range limit <a class="header-anchor" href="#range-limit">&#128279;</a></h2>
<p>Example that limit time range from <strong>8:00 AM</strong> to <strong>8:00 PM</strong>:</p>
<time-picker-limit-example ref="time-picker-limit-example"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">template</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">time-picker</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;time&quot;</span> <span class="hljs-attr">:max</span>=<span class="hljs-string">&quot;max&quot;</span> <span class="hljs-attr">:min</span>=<span class="hljs-string">&quot;min&quot;</span>/&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">script</span>&gt;</span><span class="language-javascript">
  <span class="hljs-keyword">export</span> <span class="hljs-keyword">default</span> {
    <span class="hljs-title function_">data</span> () {
      <span class="hljs-keyword">return</span> {
        <span class="hljs-attr">time</span>: <span class="hljs-keyword">new</span> <span class="hljs-title class_">Date</span>(),
        <span class="hljs-attr">min</span>: <span class="hljs-keyword">new</span> <span class="hljs-title class_">Date</span>(<span class="hljs-string">&#x27;2017/01/01 8:00&#x27;</span>), <span class="hljs-comment">// date doesn&#x27;t matter</span>
        <span class="hljs-attr">max</span>: <span class="hljs-keyword">new</span> <span class="hljs-title class_">Date</span>(<span class="hljs-string">&#x27;2017/01/01 20:00&#x27;</span>)
      }
    }
  }
</span><span class="hljs-tag">&lt;/<span class="hljs-name">script</span>&gt;</span>
<span class="hljs-comment">&lt;!-- time-picker-limit-example.vue --&gt;</span>
</code></pre>
<h2 id="readonly">Readonly <a class="header-anchor" href="#readonly">&#128279;</a></h2>
<p>All input methods are all disabled in readonly mode.</p>
<time-picker-readonly-example ref="time-picker-readonly-example"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">template</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">time-picker</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;time&quot;</span> <span class="hljs-attr">readonly</span>/&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">script</span>&gt;</span><span class="language-javascript">
  <span class="hljs-keyword">export</span> <span class="hljs-keyword">default</span> {
    <span class="hljs-title function_">data</span> () {
      <span class="hljs-keyword">return</span> {
        <span class="hljs-attr">time</span>: <span class="hljs-keyword">new</span> <span class="hljs-title class_">Date</span>()
      }
    }
  }
</span><span class="hljs-tag">&lt;/<span class="hljs-name">script</span>&gt;</span>
<span class="hljs-comment">&lt;!-- time-picker-readonly-example.vue --&gt;</span>
</code></pre>
<h2 id="with-dropdown">With dropdown <a class="header-anchor" href="#with-dropdown">&#128279;</a></h2>
<time-picker-with-dropdown ref="time-picker-with-dropdown"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">template</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">form</span> <span class="hljs-attr">class</span>=<span class="hljs-string">&quot;form-inline&quot;</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">dropdown</span> <span class="hljs-attr">class</span>=<span class="hljs-string">&quot;form-group&quot;</span>&gt;</span>
      <span class="hljs-tag">&lt;<span class="hljs-name">div</span> <span class="hljs-attr">class</span>=<span class="hljs-string">&quot;input-group&quot;</span>&gt;</span>
        <span class="hljs-tag">&lt;<span class="hljs-name">input</span> <span class="hljs-attr">class</span>=<span class="hljs-string">&quot;form-control&quot;</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;text&quot;</span> <span class="hljs-attr">:value</span>=<span class="hljs-string">&quot;this.time.toTimeString()&quot;</span> <span class="hljs-attr">readonly</span>=<span class="hljs-string">&quot;readonly&quot;</span>&gt;</span>
        <span class="hljs-tag">&lt;<span class="hljs-name">div</span> <span class="hljs-attr">class</span>=<span class="hljs-string">&quot;input-group-btn&quot;</span>&gt;</span>
          <span class="hljs-tag">&lt;<span class="hljs-name">btn</span> <span class="hljs-attr">class</span>=<span class="hljs-string">&quot;dropdown-toggle&quot;</span>&gt;</span><span class="hljs-tag">&lt;<span class="hljs-name">i</span> <span class="hljs-attr">class</span>=<span class="hljs-string">&quot;glyphicon glyphicon-time&quot;</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">i</span>&gt;</span><span class="hljs-tag">&lt;/<span class="hljs-name">btn</span>&gt;</span>
        <span class="hljs-tag">&lt;/<span class="hljs-name">div</span>&gt;</span>
      <span class="hljs-tag">&lt;/<span class="hljs-name">div</span>&gt;</span>
      <span class="hljs-tag">&lt;<span class="hljs-name">template</span> <span class="hljs-attr">slot</span>=<span class="hljs-string">&quot;dropdown&quot;</span>&gt;</span>
        <span class="hljs-tag">&lt;<span class="hljs-name">li</span> <span class="hljs-attr">style</span>=<span class="hljs-string">&quot;padding: 10px&quot;</span>&gt;</span>
          <span class="hljs-tag">&lt;<span class="hljs-name">time-picker</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;time&quot;</span>/&gt;</span>
        <span class="hljs-tag">&lt;/<span class="hljs-name">li</span>&gt;</span>
      <span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
    <span class="hljs-tag">&lt;/<span class="hljs-name">dropdown</span>&gt;</span>
  <span class="hljs-tag">&lt;/<span class="hljs-name">form</span>&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">script</span>&gt;</span><span class="language-javascript">
  <span class="hljs-keyword">export</span> <span class="hljs-keyword">default</span> {
    <span class="hljs-title function_">data</span> () {
      <span class="hljs-keyword">return</span> {
        <span class="hljs-attr">time</span>: <span class="hljs-keyword">new</span> <span class="hljs-title class_">Date</span>()
      }
    }
  }
</span><span class="hljs-tag">&lt;/<span class="hljs-name">script</span>&gt;</span>
<span class="hljs-comment">&lt;!-- time-picker-with-dropdown.vue --&gt;</span>
</code></pre>
<h2 id="custom-icons">Custom icons <a class="header-anchor" href="#custom-icons">&#128279;</a></h2>
<time-picker-icons-example ref="time-picker-icons-example"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">template</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">time-picker</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;time&quot;</span> <span class="hljs-attr">icon-control-up</span>=<span class="hljs-string">&quot;glyphicon glyphicon-plus&quot;</span> <span class="hljs-attr">icon-control-down</span>=<span class="hljs-string">&quot;glyphicon glyphicon-minus&quot;</span>/&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">script</span>&gt;</span><span class="language-javascript">
  <span class="hljs-keyword">export</span> <span class="hljs-keyword">default</span> {
    <span class="hljs-title function_">data</span> () {
      <span class="hljs-keyword">return</span> {
        <span class="hljs-attr">time</span>: <span class="hljs-keyword">new</span> <span class="hljs-title class_">Date</span>()
      }
    }
  }
</span><span class="hljs-tag">&lt;/<span class="hljs-name">script</span>&gt;</span>
<span class="hljs-comment">&lt;!-- time-picker-icons-example.vue --&gt;</span>
</code></pre>
<h2 id="without-controls">Without controls <a class="header-anchor" href="#without-controls">&#128279;</a></h2>
<time-picker-no-controls-example ref="time-picker-no-controls-example"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">template</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">time-picker</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;time&quot;</span> <span class="hljs-attr">:controls</span>=<span class="hljs-string">&quot;false&quot;</span>/&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">script</span>&gt;</span><span class="language-javascript">
  <span class="hljs-keyword">export</span> <span class="hljs-keyword">default</span> {
    <span class="hljs-title function_">data</span> () {
      <span class="hljs-keyword">return</span> {
        <span class="hljs-attr">time</span>: <span class="hljs-keyword">new</span> <span class="hljs-title class_">Date</span>()
      }
    }
  }
</span><span class="hljs-tag">&lt;/<span class="hljs-name">script</span>&gt;</span>
<span class="hljs-comment">&lt;!-- time-picker-no-controls-example.vue --&gt;</span>
</code></pre>
<h2 id="with-custom-input-width">With custom input width <a class="header-anchor" href="#with-custom-input-width">&#128279;</a></h2>
<time-picker-input-width-example ref="time-picker-input-width-example"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">template</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">time-picker</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;time&quot;</span> <span class="hljs-attr">:input-width</span>=<span class="hljs-string">&quot;inputWidth&quot;</span>/&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">script</span>&gt;</span><span class="language-javascript">
  <span class="hljs-keyword">export</span> <span class="hljs-keyword">default</span> {
    <span class="hljs-title function_">data</span> () {
      <span class="hljs-keyword">return</span> {
        <span class="hljs-attr">time</span>: <span class="hljs-keyword">new</span> <span class="hljs-title class_">Date</span>(<span class="hljs-string">&#x27;&#x27;</span>),
        <span class="hljs-attr">inputWidth</span>: <span class="hljs-number">180</span>
      }
    }
  }
</span><span class="hljs-tag">&lt;/<span class="hljs-name">script</span>&gt;</span>
<span class="hljs-comment">&lt;!-- time-picker-input-width-example.vue --&gt;</span>
</code></pre>
<h2 id="empty-fields">Empty fields <a class="header-anchor" href="#empty-fields">&#128279;</a></h2>
<time-picker-empty-fields-example ref="time-picker-empty-fields-example"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">template</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">time-picker</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;time&quot;</span>/&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">script</span>&gt;</span><span class="language-javascript">
  <span class="hljs-keyword">export</span> <span class="hljs-keyword">default</span> {
    <span class="hljs-title function_">data</span> () {
      <span class="hljs-keyword">return</span> {
        <span class="hljs-attr">time</span>: <span class="hljs-keyword">new</span> <span class="hljs-title class_">Date</span>(<span class="hljs-string">&#x27;&#x27;</span>)
      }
    }
  }
</span><span class="hljs-tag">&lt;/<span class="hljs-name">script</span>&gt;</span>
<span class="hljs-comment">&lt;!-- time-picker-empty-fields-example.vue --&gt;</span>
</code></pre>
<h1 id="api-reference">API Reference <a class="header-anchor" href="#api-reference">&#128279;</a></h1>
<h2 id="timepicker-1"><a href="https://github.com/wxsms/uiv/blob/0.x/src/components/timepicker/TimePicker.vue">TimePicker</a> <a class="header-anchor" href="#timepicker-1">&#128279;</a></h2>
<h3 id="props">Props <a class="header-anchor" href="#props">&#128279;</a></h3>
<div class="table-responsive"><table class="table table-bordered table-hover"><thead>
<tr>
<th>Name</th>
<th>Type</th>
<th>Default</th>
<th>Required</th>
<th>Description</th>
</tr>
</thead>
<tbody>
<tr>
<td><code v-pre>v-model</code></td>
<td>Date</td>
<td></td>
<td>✔</td>
<td>The selected time.</td>
</tr>
<tr>
<td><code v-pre>show-meridian</code></td>
<td>Boolean</td>
<td>true</td>
<td></td>
<td>Use 12H or 24H mode.</td>
</tr>
<tr>
<td><code v-pre>hour-step</code></td>
<td>Number</td>
<td>1</td>
<td></td>
<td>Hours to increase or decrease when using a button.</td>
</tr>
<tr>
<td><code v-pre>min-step</code></td>
<td>Number</td>
<td>1</td>
<td></td>
<td>Minutes to increase or decrease when using a button.</td>
</tr>
<tr>
<td><code v-pre>readonly</code></td>
<td>Boolean</td>
<td>false</td>
<td></td>
<td></td>
</tr>
<tr>
<td><code v-pre>max</code></td>
<td>Date</td>
<td></td>
<td></td>
<td>The maximum time that user can select or input.</td>
</tr>
<tr>
<td><code v-pre>min</code></td>
<td>Date</td>
<td></td>
<td></td>
<td>The minimum time that user can select or input.</td>
</tr>
<tr>
<td><code v-pre>icon-control-up</code></td>
<td>String</td>
<td>glyphicon glyphicon-chevron-up</td>
<td></td>
<td>The arrow icon shown inside the <code v-pre>increase</code> button.</td>
</tr>
<tr>
<td><code v-pre>icon-control-down</code></td>
<td>String</td>
<td>glyphicon glyphicon-chevron-down</td>
<td></td>
<td>The arrow icon shown inside the <code v-pre>decrease</code> button.</td>
</tr>
<tr>
<td><code v-pre>controls</code></td>
<td>Boolean</td>
<td>true</td>
<td></td>
<td>Hide the up/down <code v-pre>controls</code> if set to <code v-pre>false</code>.</td>
</tr>
<tr>
<td><code v-pre>input-width</code></td>
<td>Number</td>
<td>50</td>
<td></td>
<td>(0.34+) The width in pixels of the <code v-pre>hours</code> and <code v-pre>minutes</code> inputs.</td>
</tr>
</tbody>
</table></div></markdown-wrapper></template><script>
  

  

  

  

  

  

  

  

  
export default {components:{'time-picker-example':{"template":"<div class=\"markdown-live-example\">\n  <time-picker v-model=\"time\"/>\n</div>",
    data () {
      return {
        time: new Date()
      }
    }
  },'time-picker-24-example':{"template":"<div class=\"markdown-live-example\">\n  <time-picker v-model=\"time\" :show-meridian=\"false\"/>\n</div>",
    data () {
      return {
        time: new Date()
      }
    }
  },'time-picker-limit-example':{"template":"<div class=\"markdown-live-example\">\n  <time-picker v-model=\"time\" :max=\"max\" :min=\"min\"/>\n</div>",
    data () {
      return {
        time: new Date(),
        min: new Date('2017/01/01 8:00'), // date doesn't matter
        max: new Date('2017/01/01 20:00')
      }
    }
  },'time-picker-readonly-example':{"template":"<div class=\"markdown-live-example\">\n  <time-picker v-model=\"time\" readonly/>\n</div>",
    data () {
      return {
        time: new Date()
      }
    }
  },'time-picker-with-dropdown':{"template":"<div class=\"markdown-live-example\">\n  <form class=\"form-inline\">\n    <dropdown class=\"form-group\">\n      <div class=\"input-group\">\n        <input class=\"form-control\" type=\"text\" :value=\"this.time.toTimeString()\" readonly=\"readonly\">\n        <div class=\"input-group-btn\">\n          <btn class=\"dropdown-toggle\"><i class=\"glyphicon glyphicon-time\"></i></btn>\n        </div>\n      </div>\n      <template slot=\"dropdown\">\n        <li style=\"padding: 10px\">\n          <time-picker v-model=\"time\"/>\n        </li>\n      </template>\n    </dropdown>\n  </form>\n</div>",
    data () {
      return {
        time: new Date()
      }
    }
  },'time-picker-icons-example':{"template":"<div class=\"markdown-live-example\">\n  <time-picker v-model=\"time\" icon-control-up=\"glyphicon glyphicon-plus\" icon-control-down=\"glyphicon glyphicon-minus\"/>\n</div>",
    data () {
      return {
        time: new Date()
      }
    }
  },'time-picker-no-controls-example':{"template":"<div class=\"markdown-live-example\">\n  <time-picker v-model=\"time\" :controls=\"false\"/>\n</div>",
    data () {
      return {
        time: new Date()
      }
    }
  },'time-picker-input-width-example':{"template":"<div class=\"markdown-live-example\">\n  <time-picker v-model=\"time\" :input-width=\"inputWidth\"/>\n</div>",
    data () {
      return {
        time: new Date(''),
        inputWidth: 180
      }
    }
  },'time-picker-empty-fields-example':{"template":"<div class=\"markdown-live-example\">\n  <time-picker v-model=\"time\"/>\n</div>",
    data () {
      return {
        time: new Date('')
      }
    }
  }}}</script>